// M-116: 顧客 1 人ぶんの「商材ごとのステージ」を、顧客一覧と顧客詳細で同じ規則で解決する（pure）。
// 追加 I/O ゼロ — 保存値（interestStages / interestStageMeta）は fetchAllUsers / useCustomerDetail が
// doc を丸ごと展開した時点で AppUser に載っている。

import {
  resolveEffectiveStage,
  stageRank,
  PROMO_INTEREST_KEYS,
  type AppUser,
  type InterestStage,
  type InterestTagConfig,
  type PromoInterestKey,
} from '@kawai/shared';
import { INTEREST_STAGE_LABELS } from './segment.js';

/**
 * 減衰マーカー。**一覧のバッジ・一覧の凡例・説明ページが同じ定数を参照する。**
 *
 * ⚠ 本 issue（M-116）の教訓そのもの — 画面と説明ページが別々に文字列を持つと、
 * 片方だけが変わっても機械では検出できない。真実源を 1 つにしておけば drift が構造的に起きない。
 */
export const STAGE_DECAY_MARK = '↓';

/**
 * 「配信のしぼり込みでは <実効ステージ> として扱われます」（一覧の `title` / 顧客詳細の本文）。
 */
export function stageDecayNote(effective: InterestStage): string {
  return `配信のしぼり込みでは ${INTEREST_STAGE_LABELS[effective]} として扱われます`;
}

/**
 * 一覧の**読み上げ用**の短縮版。
 *
 * バッジ列は `<Link>` の中にあり link は name-from-content なので、行のテキスト全部が
 * リンクのアクセシブル名になる。商材ごとに長文を足すと 5 商材で 250 文字を超え、
 * 名前だけを拾う流し読みができなくなる（実測 255 文字）。全文は顧客詳細に置く。
 */
export function stageDecayNoteShort(effective: InterestStage): string {
  return `（配信では ${INTEREST_STAGE_LABELS[effective]}）`;
}

/**
 * 画面に出すステージの解決結果（M-116）。
 *
 * ⚠ **`stored` と `effective` は別物で、両方要る。**
 * - `stored` = 保存されているステージ。**時間では下がらない**（減衰は評価時にだけ効く）。
 *   ただし「到達した一番上」ではない — アンケートで「関心なし」と答えた場合などは、
 *   全件再集計のパーティションで**保存値そのものが下がる**（`promoInterest.ts` §569 /
 *   増分書込は単調 max だが再集計は置換）。
 * - `effective` = セグメントのしぼり込み・配信判定で実際に使われる減衰後のステージ
 *   （`applyStageDecay` / `matchesSegment` と同じ評価）。
 *
 * 一覧に `stored` だけを出すと、**同じ画面の「最低ステージ」で消える行**が説明できない
 * （絞り込みは `effective` で判定している / `promoSegment.ts` §113-124）。そのため
 * `decayed` を持たせ、UI 側でズレを明示できるようにしている。
 */
export interface CustomerStageView {
  key: PromoInterestKey;
  /** 保存されているステージ（時間では下がらない）。 */
  stored: InterestStage;
  /** 配信のしぼり込みで実際に使われる、減衰適用後のステージ。 */
  effective: InterestStage;
  /** 減衰で `effective` が `stored` より下がっているか。 */
  decayed: boolean;
  /** 興味の鮮度が切れて休眠扱いになっているか（`applyStageDecay` の `dormant`）。 */
  dormant: boolean;
}

/**
 * 商材 1 つぶんのステージを解決する。**出せない場合は `null`**。
 *
 * `null` になるのは次の 2 つで、どちらも「まだ判断材料が無い」を意味する:
 * - その商材の興味タグを持っていない — `matchesSegment` は `interestTags[key]` が無い顧客を
 *   無条件で対象外にするため（`promoSegment.ts` §108）、タグの無いステージは**配信で使えない**。
 *   使えない値を一覧に出すと「出ているのに対象にならない」を新しく作ることになる
 * - `interestStages[key]` が無い — `deriveInterestStages` は `attention` を出力から落とすので、
 *   キーの不在は「認知どまり（＝全員が満たす既定）」と等価。全員に `ステージ1（認知）` を
 *   出しても情報量がゼロで、バッジ列を無駄に埋めるだけ
 *
 * 顧客一覧・顧客詳細はどちらも `interestTags` を回して**キーごとに**本関数を呼ぶ。
 * まとめて配列を返す API を置かないのは、画面が出すのは「タグの並び」であって
 * 「ステージを持つ商材の並び」ではないため — 両者は一致しないので、2 つの並べ方が
 * 併存すると次の担当者がどちらを使うべきか判断できなくなる。
 *
 * ⚠ さらに**型に無い商材キーも落とす**。`interestTags` / `interestStages` は Firestore の
 * 生データを spread しただけ（`repositories/users.ts`）なので、`PromoInterestKey` は
 * **実行時には保証されない**。`resolveStageDecayRule` は `DEFAULT_INTEREST_ENGINES[key]` を
 * 素引きして `undefined` を返しうるため、未知キーだと `undefined.decay` で TypeError になり、
 * **render 中なので顧客一覧・顧客詳細が丸ごと落ちる**。ラベル表示だけだった従来は
 * 到達しなかった経路なので、ここで fail-safe に倒す。
 */
export function resolveCustomerStage(
  user: Pick<AppUser, 'interestTags' | 'interestStages' | 'interestStageMeta'>,
  key: PromoInterestKey,
  // 使うのは engines だけなので Pick で受ける（テストが config 全体を組み立てずに済む）。
  config: Pick<InterestTagConfig, 'engines'> | null | undefined,
  now: Date,
): CustomerStageView | null {
  if (!(PROMO_INTEREST_KEYS as readonly string[]).includes(key)) return null;
  if (!user.interestTags?.[key]) return null;
  const stored = user.interestStages?.[key];
  if (!stored) return null;
  // M-74: 減衰日数はテナント上書き → 商材別既定の 2 層解決（配信側と同じ入口）。
  // M-119: 合成は `resolveEffectiveStage` に一本化（配信抽出・HOT 検知・KPI リプレイと同じ定義）。
  const { stage: effective, dormant } = resolveEffectiveStage(
    { stage: stored, ...user.interestStageMeta?.[key] },
    now.toISOString(),
    key,
    config ?? undefined,
  );
  return {
    key,
    stored,
    effective,
    decayed: stageRank(effective) < stageRank(stored),
    dormant,
  };
}
